import {
  type FirestoreDataConverter,
  type Timestamp,
  collection,
  doc,
  getFirestore,
} from 'firebase/firestore';
import {getFunctions, httpsCallable} from 'firebase/functions';
import type {CourseId, UserId} from 'types/common';

/**
 * Client-side model + refs for CIPX Section Leader "Practice Teaching" (Sim
 * Section) attempts.
 *
 * The summary and attempts are written ONLY server-side by the callables (Admin
 * SDK); clients read but never write them, so an attempt is immutable to clients
 * (see firestore.rules).
 */

export type SectionTeachingAttemptStatus =
  | 'allocated'
  | 'in_progress'
  | 'completed'
  | 'abandoned'
  | 'evaluation_failed';

export type SectionTeachingSubmissionSource =
  | 'simulated_class'
  | 'solo_demo'
  | 'uploaded_video';

export interface SectionTeachingAttempt {
  attemptId: string;
  userId: UserId;
  courseId: CourseId;
  applicationRole: 'sl';
  attemptNumber: number;
  status: SectionTeachingAttemptStatus;
  scenarioId: string | null;
  /** Set by the complete callable / engine on session save. */
  sessionId?: string;
  /** Server timestamp set when Start Teaching authorized the attempt. */
  startedAt?: Timestamp;
  /**
   * `true` on attempts whose slot is charged when the take actually happens
   * rather than at the Start click. Absent on attempts that predate the field,
   * which were already counted at Start.
   */
  chargeOnEstablish?: boolean;
  /**
   * Server timestamp set once, by whichever charge path ran first (SimTeach on
   * session establish, or the complete callable). Its presence means this
   * attempt has been counted against the cap.
   */
  chargedAt?: Timestamp;
  /** Server timestamp set when the attempt was completed. */
  endedAt?: Timestamp;
  /**
   * How the applicant taught. Surfaced to reviewers only as a neutral fairness
   * note (text is an accessibility accommodation, not a quality signal).
   */
  inputMode?: 'voice' | 'text';
  /** How this reviewable recording was made. Absent on legacy attempts. */
  submissionSource?: SectionTeachingSubmissionSource;
  /**
   * `true` for legacy CIPX attempts completed after the simulated student's
   * closing question. PAI source-tagged class/solo/upload attempts set false
   * because that question is not their completion criterion.
   */
  answeredClosingQuestion?: boolean;
  /**
   * Outcome of the recording pipeline (issue #4193), written only by the
   * server. Kept untyped here and parsed through `readCanonicalStatus` (or
   * the contract's `parseCanonicalStatus`), since the converter casts rather
   * than validates.
   */
  canonical?: unknown;
  /** The closing question the applicant answered, when known. */
  questionId?: string;
  /** `false` marks a solo (no simulated students) attempt. Unset on attempts
   *  that predate the field (all simulated). */
  simulatedStudents?: boolean;
  /** The applicant's optional solo note. Unlike the typing accommodation's
   *  private note, this IS shown to reviewers. */
  soloNote?: string;
}

const SECTION_TEACHING_ATTEMPTS_DOC = 'sectionTeachingAttempts';

/**
 * Summary doc id the staff demo-teach lifecycle lives under (issue #4109) —
 * same layout as `sectionTeachingAttempts`, separate document, so a staff
 * rehearsal can never touch real application state. Mirror of the functions
 * package's `DEMO_TEACH_ATTEMPTS_DOC`; kept in sync by hand.
 */
const DEMO_TEACH_ATTEMPTS_DOC = 'demoTeachAttempts';

const ATTEMPTS_SUBCOLLECTION = 'attempts';

/**
 * Options accepted by every ref helper below.
 */
export interface SectionAttemptRefOptions {
  /**
   * `true` reads the staff demo collection (`demoTeachAttempts`) instead of
   * the applicant one. Omitted → the real applicant collection.
   */
  demo?: boolean;
}

const getSummaryPath = (
  userId: UserId,
  courseId: CourseId,
  options?: SectionAttemptRefOptions,
): string =>
  `users/${userId}/${courseId}/${
    options?.demo === true ? DEMO_TEACH_ATTEMPTS_DOC : SECTION_TEACHING_ATTEMPTS_DOC
  }`;

const sectionTeachingAttemptConverter: FirestoreDataConverter<SectionTeachingAttempt> =
  {
    toFirestore: attempt => ({...attempt}),
    fromFirestore: (snapshot, options) =>
      snapshot.data(options) as SectionTeachingAttempt,
  };

/**
 * The `attempts` subcollection ref for one applicant + target course. Read-only
 * to clients (firestore.rules); attempts are minted by the callables.
 *
 * @param userId - The applicant whose attempts to read.
 * @param courseId - The target course the applicant is applying to.
 * @param options - See {@link SectionAttemptRefOptions}. Omitted → the real
 *   applicant collection.
 * @return A typed collection reference to the applicant's attempts.
 */
export const getSectionTeachingAttemptsCollectionRef = (
  userId: UserId,
  courseId: CourseId,
  options?: SectionAttemptRefOptions,
) =>
  collection(
    getFirestore(),
    `${getSummaryPath(userId, courseId, options)}/${ATTEMPTS_SUBCOLLECTION}`,
  ).withConverter(sectionTeachingAttemptConverter);

/**
 * Whether this attempt has been counted against the cap.
 *
 * The mirror of the functions app's `shouldChargeSectionAttempt` (and of the
 * simteach app's `section_attempt_charge_decision`), read from the other side:
 * those decide whether a slot still needs charging, this asks whether one
 * already was. An attempt WITHOUT `chargeOnEstablish` predates the deferred
 * charge and was counted by `begin` at the Start click; one that carries the
 * marker counts only once something stamped `chargedAt`.
 *
 * Use this, not `status`, anywhere the UI means "this take cost the applicant
 * something". Since the charge moved off the Start click, an `in_progress`
 * attempt is no longer proof of that: a connect that never established leaves
 * one behind and costs nothing.
 *
 * @param attempt - The attempt to test.
 * @return `true` when `summary.attemptCount` includes this attempt.
 * @throws Never.
 */
export const wasSectionAttemptCharged = (
  attempt: SectionTeachingAttempt,
): boolean =>
  attempt.chargeOnEstablish !== true ||
  // The converter is a raw cast, so an absent field arrives as undefined; a
  // merge that wrote the field empty would arrive as null. Neither is charged.
  (attempt.chargedAt !== undefined && attempt.chargedAt !== null);

/**
 * Fields the callables keep on the summary document. Read-only to the client,
 * used to preview remaining attempts and the chosen final attempt.
 */
export interface SectionTeachingAttemptSummary {
  /**
   * Number of CHARGED attempts — the cap counter. Neither allocating nor
   * starting consumes a slot: it is charged when the take actually happens,
   * by SimTeach when a simulated-class session establishes, or by the complete
   * callable for solo/uploaded-video takes (and as a backstop). A Start whose
   * connection never established is therefore never counted.
   */
  attemptCount?: number;
  maxAttempts?: number;
  /** Monotonic count of attempts ever allocated (used only for numbering). */
  totalAllocated?: number;
  /** The attempt the applicant picked for reviewers to grade, if any. */
  finalAttemptId?: string;
  /** The most recently allocated attempt id, if any. */
  latestAttemptId?: string;
}

const summaryConverter: FirestoreDataConverter<SectionTeachingAttemptSummary> =
  {
    toFirestore: summary => ({...summary}),
    fromFirestore: (snapshot, options) =>
      snapshot.data(options) as SectionTeachingAttemptSummary,
  };

/**
 * The summary doc ref for one applicant + target course.
 *
 * @param userId - The applicant whose summary to read.
 * @param courseId - The target course the applicant is applying to.
 * @param options - See {@link SectionAttemptRefOptions}. Omitted → the real
 *   applicant collection.
 * @return A typed document reference to the applicant's attempt summary.
 */
export const getSectionTeachingAttemptSummaryRef = (
  userId: UserId,
  courseId: CourseId,
  options?: SectionAttemptRefOptions,
) =>
  doc(getFirestore(), getSummaryPath(userId, courseId, options)).withConverter(
    summaryConverter,
  );

/**
 * =============================================================================
 * IMPORTANT, READ THIS MESSAGE BEFORE EDITING THE TYPES BELOW.
 *
 * They need to be kept tightly in sync with their corresponding version in the
 * functions package. If you are changing them, please also make the
 * corresponding changes in
 * `src/apps/functions/src/sl/sectionTeachingAttemptApi.ts` and submit both
 * changes in the same PR.
 * =============================================================================
 */

export interface AllocateSectionTeachingAttemptRequest {
  targetCourseId: CourseId;
  /**
   * Client generated id for this begin click. A retry after a lost response
   * replays the already allocated attempt instead of burning a second one.
   */
  requestId: string;
  /** The section scenario/assignment being taught. */
  scenarioId?: string;
  /**
   * `true` targets the staff demo collection (`demoTeachAttempts`) — routed
   * and role-gated server-side; see the functions package's
   * `AllocateSectionTeachingAttemptRequest.demo`. Omitted → the real
   * applicant collection.
   */
  demo?: boolean;
}

export interface AllocateSectionTeachingAttemptResponse {
  attemptId: string;
  attemptNumber: number;
  maxAttempts: number;
  attemptsRemaining: number;
}

export interface BeginSectionTeachingAttemptRequest {
  targetCourseId: CourseId;
  /** The attempt to start (previously minted by allocate). */
  attemptId: string;
  /**
   * Client generated id for this "Start Teaching" click. A retry after a lost
   * response replays against the already started attempt instead of double
   * counting.
   */
  requestId: string;
  /** The section scenario/assignment being taught. */
  scenarioId?: string;
  /**
   * `true` for a take that will never open a SimTeach session — a solo demo or
   * an uploaded video — so its slot is counted at this click rather than
   * waiting for a session that is never going to establish. Send it from every
   * such path; omitting it defers the charge to `complete`, which means an
   * abandoned take costs nothing.
   */
  chargeAtStart?: boolean;
  /**
   * `true` targets the staff demo collection (`demoTeachAttempts`) — routed
   * and role-gated server-side; see the functions package's
   * `AllocateSectionTeachingAttemptRequest.demo`. Omitted → the real
   * applicant collection.
   */
  demo?: boolean;
}

export interface BeginSectionTeachingAttemptResponse {
  attemptId: string;
  status: 'in_progress';
  attemptsRemaining: number;
  maxAttempts: number;
}

/** Session evidence submitted with completion; mirrors the functions guard. */
export interface SectionSessionEvidencePayload {
  /** Epoch at which the recording began, anchoring every event below. */
  sessionStartEpochMs: number;
  /** Captured speech stretches; the server bounds and stores them. */
  speakingIntervals: {
    speaker: {kind: 'applicant'} | {kind: 'student'; studentId: number};
    startEpochMs: number;
    endEpochMs: number;
  }[];
  /** Captured hand lifecycle events; the server bounds and stores them. */
  handEvents: {
    kind: 'raised' | 'called' | 'expired';
    studentId: number;
    turnId: string;
    atEpochMs: number;
    /**
     * Set on the raise the session recognized as the timed closing question,
     * so review reads which turn it was instead of inferring it.
     */
    closingQuestion?: boolean;
  }[];
}

export interface CompleteSectionTeachingAttemptRequest {
  targetCourseId: CourseId;
  attemptId: string;
  scenarioId?: string;
  sessionId?: string;
  inputMode?: 'voice' | 'text';
  submissionSource?: SectionTeachingSubmissionSource;
  questionId?: string;
  /**
   * Client-observed session facts the review side cannot recover from a
   * finished recording. Optional and fail-soft on the server: completion
   * stands even when the evidence cannot be stored, and the server drops a
   * malformed payload rather than rejecting the completion.
   */
  sessionEvidence?: SectionSessionEvidencePayload;
  /** `false` marks a solo (no simulated students) attempt. */
  simulatedStudents?: boolean;
  /** Optional reviewer-visible note on the solo opt-out (max 500 chars). */
  soloNote?: string;
  /**
   * `true` targets the staff demo collection (`demoTeachAttempts`) — routed
   * and role-gated server-side; see the functions package's
   * `AllocateSectionTeachingAttemptRequest.demo`. Omitted → the real
   * applicant collection.
   */
  demo?: boolean;
}

export interface CompleteSectionTeachingAttemptResponse {
  attemptId: string;
  status: 'completed';
}

export interface SelectSectionTeachingAttemptRequest {
  targetCourseId: CourseId;
  attemptId: string;
  /**
   * `true` targets the staff demo collection (`demoTeachAttempts`) — routed
   * and role-gated server-side; see the functions package's
   * `AllocateSectionTeachingAttemptRequest.demo`. Omitted → the real
   * applicant collection.
   */
  demo?: boolean;
}

export interface SelectSectionTeachingAttemptResponse {
  /** The completed attempt now selected for review. */
  finalAttemptId: string;
}

/**
 * Request contract for the `deletesectionteachingattempt` callable.
 * Admin-only: the caller must be `Role.ADMIN`+ in `targetCourseId`, checked
 * server-side regardless of what the client renders.
 */
export interface DeleteSectionTeachingAttemptRequest {
  targetCourseId: CourseId;
  /** The attempt to delete, owned by the caller. */
  attemptId: string;
  /**
   * `true` targets the staff demo collection — semantics on
   * {@link AllocateSectionTeachingAttemptRequest.demo}. Omitted → the real
   * applicant collection.
   */
  demo?: boolean;
}

/** Response contract for the `deletesectionteachingattempt` callable. */
export interface DeleteSectionTeachingAttemptResponse {
  attemptId: string;
  deleted: true;
}

/** Error-details marker the callable sends when the retry cap is reached. */
export const SECTION_ATTEMPTS_EXHAUSTED_REASON = 'attempts_exhausted';

/**
 * Error-details marker the `begin` callable sends when the applicant tries to
 * start before completing the gating (`lockedUntilComplete`) lesson. Mirror of
 * the functions-side `SECTION_PREREQUISITE_INCOMPLETE_REASON`; kept in sync by
 * hand.
 */
export const SECTION_PREREQUISITE_INCOMPLETE_REASON = 'prerequisite_incomplete';

/**
 * Allocates a new section-teaching attempt (server-enforced cap).
 *
 * @param request - The target course, an idempotency `requestId`, and the
 *   scenario being taught.
 * @return The minted attempt id + number and the remaining-attempt counts.
 * @throws A Firebase callable error; `resource-exhausted` with
 *   `details.reason === SECTION_ATTEMPTS_EXHAUSTED_REASON` when the cap is hit.
 */
export const allocateSectionTeachingAttempt = async (
  request: AllocateSectionTeachingAttemptRequest,
): Promise<AllocateSectionTeachingAttemptResponse> => {
  const callable = httpsCallable<
    AllocateSectionTeachingAttemptRequest,
    AllocateSectionTeachingAttemptResponse
  >(getFunctions(), 'allocatesectionteachingattempt');
  const result = await callable(request);
  return result.data;
};

/**
 * Begins a section-teaching attempt ("Start Teaching"), the point at which a
 * slot is consumed (server-enforced cap). Idempotent: a retry against an
 * already-started attempt returns the current counts without double counting.
 *
 * @param request - The target course, the attempt id, an idempotency
 *   `requestId`, and the scenario being taught.
 * @return The attempt id, its new `in_progress` status, and remaining counts.
 * @throws A Firebase callable error; `resource-exhausted` with
 *   `details.reason === SECTION_ATTEMPTS_EXHAUSTED_REASON` when the cap is hit,
 *   `not-found` when the attempt does not exist for this user.
 */
export const beginSectionTeachingAttempt = async (
  request: BeginSectionTeachingAttemptRequest,
): Promise<BeginSectionTeachingAttemptResponse> => {
  const callable = httpsCallable<
    BeginSectionTeachingAttemptRequest,
    BeginSectionTeachingAttemptResponse
  >(getFunctions(), 'beginsectionteachingattempt');
  const result = await callable(request);
  return result.data;
};

/**
 * Marks a section-teaching attempt completed (the applicant answered the
 * student's closing question). Turns on the application checklist's green check.
 *
 * @param request - The target course, the attempt id, and optional
 *   scenario/session/input-mode/question metadata to record.
 * @return The attempt id and its new `completed` status.
 * @throws A Firebase callable error; `not-found` when the attempt does not
 *   exist for this user.
 */
export const completeSectionTeachingAttempt = async (
  request: CompleteSectionTeachingAttemptRequest,
): Promise<CompleteSectionTeachingAttemptResponse> => {
  const callable = httpsCallable<
    CompleteSectionTeachingAttemptRequest,
    CompleteSectionTeachingAttemptResponse
  >(getFunctions(), 'completesectionteachingattempt');
  const result = await callable(request);
  return result.data;
};

/**
 * Sets which completed attempt the applicant wants reviewers to grade.
 * Selecting the same attempt again is an idempotent no-op.
 *
 * @param request - The target course and completed attempt to select.
 * @return The resulting `finalAttemptId`.
 * @throws A Firebase callable error; `failed-precondition` when the attempt is
 *   not completed, `not-found` when it does not exist for this user.
 */
export const selectSectionTeachingAttempt = async (
  request: SelectSectionTeachingAttemptRequest,
): Promise<SelectSectionTeachingAttemptResponse> => {
  const callable = httpsCallable<
    SelectSectionTeachingAttemptRequest,
    SelectSectionTeachingAttemptResponse
  >(getFunctions(), 'selectsectionteachingattempt');
  const result = await callable(request);
  return result.data;
};

/**
 * Deletes a section-teaching attempt. Admin-only (enforced server-side); an
 * ordinary applicant's call is rejected with `permission-denied` even against
 * their own attempts.
 *
 * @param request - The target course, the attempt id, and whether it lives in
 *   the staff demo collection.
 * @return The deleted attempt id.
 * @throws A Firebase callable error; `permission-denied` when the caller is
 *   below admin in the target course, `not-found` when the attempt does not
 *   exist for this user.
 */
export const deleteSectionTeachingAttempt = async (
  request: DeleteSectionTeachingAttemptRequest,
): Promise<DeleteSectionTeachingAttemptResponse> => {
  const callable = httpsCallable<
    DeleteSectionTeachingAttemptRequest,
    DeleteSectionTeachingAttemptResponse
  >(getFunctions(), 'deletesectionteachingattempt');
  const result = await callable(request);
  return result.data;
};
